import type { ApolloError } from '@apollo/client';
import { useState } from 'react';

import type { GetExternalPreviewLinkQuery } from '../../../apis/graphql';
import { campaignClient, getErrorMessageFromGraphQlErrors } from '../../../apis/graphql';
import { getGraphQLErrorExtensionsMessage, showFailure } from '../../utils';
import {
  EXPERIENCE_ID_MISSING_MESSAGE,
  FETCH_EXTERNAL_PREVIEW_LINK_FAIL_MESSAGE,
  PREVIEW_LINK_IS_NOT_AVAILABLE_MESSAGE,
} from '../constants';
import { GET_EXTERNAL_PREVIEW_LINK } from './queries/getExternalPreviewLink';

export type UseFetchExternalPreviewLink = {
  isLoading: boolean;
  onFetchExperiencePreviewLink: (experienceId?: string | null) => Promise<void>;
};

export const useFetchExternalPreviewLink = (): UseFetchExternalPreviewLink => {
  const [loading, setLoading] = useState(false);

  const onFetchExperiencePreviewLink = async (experienceId?: string | null): Promise<void> => {
    if (!experienceId) {
      showFailure({
        message: FETCH_EXTERNAL_PREVIEW_LINK_FAIL_MESSAGE,
        description: EXPERIENCE_ID_MISSING_MESSAGE,
      });
      return;
    }

    setLoading(true);

    try {
      const { data, errors } = await campaignClient.query<GetExternalPreviewLinkQuery>({
        query: GET_EXTERNAL_PREVIEW_LINK,
        variables: { experienceId },
      });

      const externalPreviewLink = data?.getExternalPreviewLink?.externalPreview;

      const hasErrors = !!errors?.length;

      if (hasErrors) {
        showFailure({
          message: FETCH_EXTERNAL_PREVIEW_LINK_FAIL_MESSAGE,
          description: getErrorMessageFromGraphQlErrors(errors ?? []),
        });
        return;
      }

      if (!externalPreviewLink) {
        showFailure({
          message: FETCH_EXTERNAL_PREVIEW_LINK_FAIL_MESSAGE,
          description: PREVIEW_LINK_IS_NOT_AVAILABLE_MESSAGE,
        });
        return;
      }

      window.open(externalPreviewLink, '_blank');
    } catch (error) {
      showFailure({
        message: FETCH_EXTERNAL_PREVIEW_LINK_FAIL_MESSAGE,
        description: getGraphQLErrorExtensionsMessage(error as ApolloError) ?? '',
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    isLoading: loading,
    onFetchExperiencePreviewLink,
  };
};
